import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'
import { IUser } from 'redux/store/user/types'
import { ROLES } from 'features/Permission'
import { isInsideCommunityCondition } from 'features/Permission/abilities/conditions'
import { ACTIONS, SUBJECTS } from './constants'
import {
  FinancialsAbilityBuilder,
  FinancialsAbility,
  PossibleAbilities,
  Conditions
} from './types'

const ContractAbilityClass = Ability as AbilityClass<FinancialsAbility>

export const ability = new Ability<PossibleAbilities, Conditions>()

export default (user: IUser) => {
  const builder = new AbilityBuilder(ContractAbilityClass)

  switch (user.role) {
    case ROLES.VENDOR_CONTRACT_ADMIN:
    case ROLES.VENDOR_FINANCE_OFFICER:
    case ROLES.VENDOR_ANALYST:
    case ROLES.VENDOR_CONTRACT_STEWARD:
      defineVendorsAbilities(builder, user)
      break
    case ROLES.COMMONS_VICE_PRESIDENT:
    case ROLES.COMMONS_CONTRACT_STEWARD:
    case ROLES.COMMONS_ANALYST:
      defineCognusCommonsAbilities(builder, user)
      break
    case ROLES.COMMUNITY_PRESIDENT:
    case ROLES.VICE_PRESIDENT:
    case ROLES.CONTRACT_STEWARD:
    case ROLES.ANALYST:
    case ROLES.COMMUNITY_RCA:
    case ROLES.FINANCE:
      defineCognusCommunityAbilities(builder, user)
      break
    case ROLES.GPO_PRESIDENT:
    case ROLES.GPO_CONTRACT_STEWARD:
    case ROLES.GPO_FINANCE_OFFICER:
    case ROLES.GPO_ANALYST:
      defineGpoAbilities(builder, user)
  }

  return new Ability(builder.rules) as FinancialsAbility
}

const defineVendorsAbilities = (
  builder: FinancialsAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_DASHBOARD)
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_INVOICES)
  builder.can(ACTIONS.VIEW, SUBJECTS.MARKETPLACE_AR)
  builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_HS_NAME)
  builder.can(ACTIONS.DISPUTE, SUBJECTS.INVOICE)
  builder.can(ACTIONS.VIEW, SUBJECTS.INVOICE_DETAILS)
  //builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_TAB)
  builder.can(ACTIONS.INITIATE, SUBJECTS.ADMIN_PAYMENT)
  if (
    user.role === ROLES.VENDOR_CONTRACT_ADMIN ||
    user.role === ROLES.VENDOR_FINANCE_OFFICER
  ) {
    builder.can(ACTIONS.INITIATE, SUBJECTS.MARKETPLACE_AR_PAYMENT)
    builder.can(ACTIONS.INITIATE, SUBJECTS.REBATES_PAYMENT)
    builder.can(ACTIONS.INITIATE, SUBJECTS.COGNUS_AR_INVOICES_INITIATE_DISPUTE)
  }
  if (
    user.role === ROLES.VENDOR_CONTRACT_ADMIN ||
    user.role === ROLES.VENDOR_FINANCE_OFFICER ||
    user.role === ROLES.VENDOR_ANALYST ||
    user.role === ROLES.VENDOR_CONTRACT_STEWARD
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_INVOICES_DETAILS)
  }
}

const defineCognusCommonsAbilities = (
  builder: FinancialsAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_DASHBOARD)
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_INVOICES)
  builder.can(ACTIONS.VIEW, SUBJECTS.MARKETPLACE_AR)
  builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.INVOICE_DETAILS)
  //builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_TAB)
  if (user.role === ROLES.COMMONS_VICE_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SPLIT_FEES)
    builder.can(ACTIONS.EDIT, SUBJECTS.ADMIN_SPLIT_FEES)
    builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_GPO_TAB)
    builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_COMMUNITY_TAB)
    builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_PROCESSING_TAB)
    builder.can(ACTIONS.VIEW, SUBJECTS.GPO_COMMUNITY_DATA)
  }
  if (
    user.role === ROLES.COMMONS_VICE_PRESIDENT ||
    user.role === ROLES.COMMONS_CONTRACT_STEWARD ||
    user.role === ROLES.COMMONS_ANALYST
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_INVOICES_DETAILS)
  }
}

const defineCognusCommunityAbilities = (
  builder: FinancialsAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_DASHBOARD)
  builder.can(ACTIONS.VIEW, SUBJECTS.MARKETPLACE_AR)
  //builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_TAB)
  if (user.role !== ROLES.COMMUNITY_RCA) {
    builder.can(
      ACTIONS.VIEW,
      SUBJECTS.REBATES_HS_NAME,
      isInsideCommunityCondition
    )
  }
  if (user.role !== ROLES.COMMUNITY_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.REBATES_INVOICES)
  }
  if (user.role === ROLES.FINANCE || user.role === ROLES.COMMUNITY_PRESIDENT) {
    builder.can(
      ACTIONS.VIEW,
      SUBJECTS.ADMIN_HS_NAME,
      isInsideCommunityCondition
    )
  }
  if (user.role === ROLES.COMMUNITY_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS)
    builder.can(
      ACTIONS.VIEW,
      SUBJECTS.COGNUS_AR_HS_NAME,
      isInsideCommunityCondition
    )
  }
  if (user.role !== ROLES.COMMUNITY_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.INVOICE_DETAILS)
    builder.can(ACTIONS.DISPUTE, SUBJECTS.INVOICE)
  }
  if (user.role === ROLES.COMMUNITY_PRESIDENT) {
    builder.can(ACTIONS.INITIATE, SUBJECTS.ADMIN_PAYMENT)
  }
  if (
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.COMMUNITY_RCA ||
    user.role === ROLES.FINANCE
  ) {
    builder.can(ACTIONS.INITIATE, SUBJECTS.PAYMENT)
  }
  if (
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.COMMUNITY_PRESIDENT
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SPLIT_FEES)
    builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_GPO_TAB)
    builder.can(
      ACTIONS.VIEW,
      SUBJECTS.SPLIT_FEES_COMMUNITY_TAB,
      isInsideCommunityCondition
    )
    builder.can(
      ACTIONS.VIEW,
      SUBJECTS.GPO_COMMUNITY_DATA,
      isInsideCommunityCondition
    )
  }
  if (user.role === ROLES.FINANCE) {
    builder.can(ACTIONS.INITIATE, SUBJECTS.COMPLETE_REBATES_PAYMENT)
    builder.can(ACTIONS.INITIATE, SUBJECTS.COMPLETE_MARKETPLACE_AR_PAYMENT)
  }
  if (
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.FINANCE ||
    user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.COMMUNITY_RCA ||
    user.role === ROLES.ANALYST
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_INVOICES_DETAILS)
  }
}

const defineGpoAbilities = (builder: FinancialsAbilityBuilder, user: IUser) => {
  //builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_TAB)
  builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SPLIT_FEES)
  builder.can(ACTIONS.VIEW, SUBJECTS.COGNUS_AR_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_HS_NAME)
  builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_GPO_TAB)
  builder.can(ACTIONS.VIEW, SUBJECTS.SPLIT_FEES_COMMUNITY_TAB)
  builder.can(
    ACTIONS.VIEW,
    SUBJECTS.GPO_COMMUNITY_DATA,
    isInsideCommunityCondition
  )
  if (user.role === ROLES.GPO_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS)
  }
  if (user.role === ROLES.GPO_CONTRACT_STEWARD) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS)
  }
  if (user.role === ROLES.GPO_ANALYST) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS)
  }
  if (user.role === ROLES.GPO_FINANCE_OFFICER) {
    builder.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS)
  }
}
